import type { RoutineInput } from '@fitness/api-client';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { RoutineEditor } from '@/components/routine/RoutineEditor';
import { describeError } from '@/lib/api';
import { SAVED, showToast } from '@/lib/toast';
import { useReplaceRoutine, useRoutine } from '@/lib/queries/routines';

export const Route = createFileRoute('/_app/routines/$id/edit')({
  component: EditRoutinePage,
});

function EditRoutinePage() {
  const { id } = Route.useParams();
  const query = useRoutine(id);

  return (
    <>
      <PageHeader title="Editar rutina" subtitle={query.data?.name} />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando la rutina…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar la rutina"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <EditorWithData id={id} routine={query.data} />
      )}
    </>
  );
}

function EditorWithData({
  id,
  routine,
}: {
  id: string;
  routine: NonNullable<ReturnType<typeof useRoutine>['data']>;
}) {
  const navigate = useNavigate();
  const replace = useReplaceRoutine(id);

  function handleSave(input: RoutineInput): void {
    replace.mutate(input, {
      onSuccess: () => {
        showToast(SAVED.routineSaved);
        void navigate({ to: '/routines/$id', params: { id } });
      },
    });
  }

  return (
    <RoutineEditor
      routine={routine}
      onSave={handleSave}
      saving={replace.isPending}
      errorMessage={replace.isError ? describeError(replace.error, { saving: true }) : undefined}
      submitLabel="Guardar cambios"
      onCancel={() => void navigate({ to: '/routines/$id', params: { id } })}
    />
  );
}
